import { FC } from 'react';
import { RemoveAlertDialog, useBoolean } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';
import { putInsurancePolicyPackage } from '@parties/src/features/detail/api/insurance-policies/putInsurancePolicyPackageForParty';
import useflowsProductBasketStore from '@flows/src/store/zustand/flows-products/flowsProductBasketStore';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import { putInsurancePolicyForSimulation } from '@flows/src/api/anva-flows/simulation/putInsurancePolicyForSimulation';
import { v4 } from 'uuid';
import cogoToast from 'cogo-toast';
import { IdResponse } from '../../../../../../../types/API';
import {
  AnvaQuotesCompletePayload,
  completeConversation,
} from '@conversations/src/features/detail/shared/api/completeConversation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

const FinishBasketModal: FC<Props> = ({ isOpen, onClose, sessionId }) => {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useBoolean(false);
  const { flowId } = useParams<{ flowId: string }>();
  const { data: sessionData } = useFlowsSession(sessionId);
  const history = useHistory();
  const storePacks = useflowsProductBasketStore((state) => state.packages);
  const storeInsurances = useflowsProductBasketStore(
    (state) => state.insurances,
  );

  const handleFinish = async () => {
    // Create insurance policies/packages by sessionId
    const partyId = sessionData?.prospect?.id;
    if (!partyId) return;
    try {
      setIsCreating.on();
      const completionPayload: AnvaQuotesCompletePayload = [];
      await Promise.all(
        storePacks.map((pack) => {
          const calc = async () => {
            const createdPackage = await putInsurancePolicyPackage({
              partyId: partyId,
              insurancePackage: {
                ...pack,
                premium: pack.quotes.reduce(
                  (acc, { total_premium }) => acc + total_premium,
                  0,
                ),
                name: pack.name,
                external_refs: [
                  {
                    ref: pack.code,
                    company: pack.quotes[0]?.insurance_company?.name,
                  },
                ],
              },
            });
            const quotes = await Promise.all(
              pack.quotes.map((quote) =>
                putInsurancePolicyForSimulation({
                  partyId: partyId,
                  sessionId: sessionId,
                  payload: {
                    name: quote.name,
                    insurance_company: quote.insurance_company.name,
                    coverages: quote.coverages.map((c) => ({
                      id: v4(),
                      name: c.name,
                      premium: c.total_premium,
                      external_labels: [
                        {
                          label: c.code,
                          type: 'ANVA',
                        },
                      ],
                    })),
                    documents: [],
                    payment_interval: quote.payment_interval,
                    policy_package_id: createdPackage.id,
                    links: quote.documents.map((doc) => ({
                      filename: doc.name,
                      link: doc.link,
                    })),
                    premium: quote.total_premium,
                    external_refs: [
                      {
                        ref: quote.anva_product_id,
                        company: quote.insurance_company.name,
                      },
                    ],
                    anva_product_id: quote.anva_product_id,
                    anva_collection_method_code:
                      quote.anva_collection_method_code,
                  },
                }),
              ),
            );
            /** Add insurance policy id to selected store to pass to complete call on the next page */
            (quotes as IdResponse[]).forEach((quote, i) => {
              const selectedMatched = pack.quotes[i];
              if (selectedMatched) {
                completionPayload.push({
                  anva_product_id: selectedMatched.anva_product_id,
                  index: selectedMatched.index,
                  insurance_policy_id: quote.id,
                });
              }
            });
          };
          return calc();
        }),
      );
      /** Individual insurances */
      const quotes = await Promise.all(
        storeInsurances.map((quote) =>
          putInsurancePolicyForSimulation({
            partyId: partyId,
            sessionId: sessionId,
            payload: {
              name: quote.name,
              insurance_company: quote.insurance_company.name,
              coverages: quote.coverages.map((c) => ({
                id: v4(),
                name: c.name,
                premium: c.total_premium,
                external_labels: [
                  {
                    label: c.code,
                    type: 'ANVA',
                  },
                ],
              })),
              documents: [],
              payment_interval: quote.payment_interval,
              links: quote.documents.map((doc) => ({
                filename: doc.name,
                link: doc.link,
              })),
              premium: quote.total_premium,
              external_refs: [
                {
                  ref: quote.anva_product_id,
                  company: quote.insurance_company.name,
                },
              ],
              anva_product_id: quote.anva_product_id,
              anva_collection_method_code: quote.anva_collection_method_code,
            },
          }),
        ),
      );
      /** Add insurance policy id to selected store to pass to complete call on the next page */
      (quotes as IdResponse[]).forEach((quote, i) => {
        const selectedMatched = storeInsurances[i];
        if (selectedMatched) {
          completionPayload.push({
            anva_product_id: selectedMatched.anva_product_id,
            index: selectedMatched.index,
            insurance_policy_id: quote.id,
          });
        }
      });
      // complete conversation
      await completeConversation({
        conversationId: sessionId,
        anva_quotes: completionPayload,
      });
      // handle quotes
      history.push({
        pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/selection-overview`,
        search: window.location.search,
      });
    } catch {
      cogoToast.error(t('Something went wrong!'));
    } finally {
      setIsCreating.off();
    }
  };

  return (
    <RemoveAlertDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('FLOWS_PRODUCT-BASKET_ACTION-FINISH_MODAL_TITLE')}
      description={t('FLOWS_PRODUCT-BASKET_ACTION-FINISH_MODAL_DESCRIPTION')}
      variant="primary"
      confirmButtonText={t('FLOWS_PRODUCT-BASKET_ACTION-FINISH_MODAL_ACTION')}
      isLoading={isCreating}
      onConfirm={handleFinish}
    />
  );
};

export default FinishBasketModal;
